import { useSelectedGuessCache } from "@/store/selected-guess-cache";
import { get } from "@/utils/utils";
import { useEffect, useRef, useState } from "react";
import type { GuessItem } from "../types";

/**
 * 管理选中的竞猜状态（含缓存逻辑）
 */
export function useSelectedGuessState(
    eventNo: string | null,
    guessList: GuessItem[],
    eventData: any,
) {
    const [ selectedGuessNo, setSelectedGuessNo ] = useState<string | null>( null );
    const [ selectedBetType, setSelectedBetType ] = useState<1 | 2 | null>( null );

    const { clearCache } = useSelectedGuessCache();
    const cachedEventNo = useSelectedGuessCache( ( state ) => state.currentEventNo );
    const cachedGuess = useSelectedGuessCache( ( state ) => state.selectedGuess );
    const prevEventNoRef = useRef<string | null>( null );

    // 当 eventNo 变化时清除缓存
    useEffect( () => {
        if ( eventNo && prevEventNoRef.current !== null && prevEventNoRef.current !== eventNo ) {
            clearCache();
        }
        prevEventNoRef.current = eventNo;
    }, [ eventNo, clearCache ] );

    // 根据缓存或默认值确定选中项
    useEffect( () => {
        if ( !eventData || !guessList.length || !eventNo ) {
            return;
        }

        // 尝试从缓存获取
        if ( cachedEventNo === eventNo && cachedGuess ) {
            const targetGuess = guessList.find(
                ( g ) => get( g, "guessNo", "" ) === cachedGuess.guessNo,
            );
            if ( targetGuess ) {
                const status = get( targetGuess, "status", 0 );
                const isEnded = status === 2 || status === 3;
                if ( !isEnded ) {
                    setSelectedGuessNo( cachedGuess.guessNo );
                    setSelectedBetType( cachedGuess.betType );
                    return;
                }
            }
        }

        // 缓存无效，选择第一个可用的竞猜
        const firstAvailableGuess = guessList.find( ( guess ) => {
            const status = get( guess, "status", 0 );
            return status !== 2 && status !== 3;
        } );

        if ( firstAvailableGuess ) {
            const guessNo = get( firstAvailableGuess, "guessNo", "" );
            if ( guessNo ) {
                setSelectedGuessNo( guessNo );
                setSelectedBetType( 1 ); // Yes
            }
        }
    }, [ eventData, guessList, eventNo, cachedEventNo, cachedGuess ] );

    return {
        selectedGuessNo,
        selectedBetType,
    };
}
